import { FC, useMemo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Form, Select, FormInstance, Tooltip } from 'antd';
import { RootState } from '../../../store';
import {
  FormChild,
  FormFieldType,
  Option,
  FormDetailParams,
  FieldChanges,
  ApprovalType,
} from '../types';
import {
  getOptions,
  parseMetadataDependencies,
  areItemsDifferent,
} from '../utils';
import styles from '../formBuilderDetail.module.scss';

import FormItemLabel from './FormItemLabel';
import FormItemDisplayAs from './FormItemDisplayAs';
import FormItemContent from './FormItemContent';
import FormItemAttachments from './FormItemAttachments';
import FormItemAnswers from './FormItemAnswers';
import FormItemPhotoCount from './FormItemPhotoCount';
import FormItemReqAnswersCount from './FormItemReqAnswersCount';
import FormItemRequired from './FormItemRequired';
import FormItemLegalText from './FormItemLegalText';
import FormItemApprovalReq from './FormItemApprovalReq';
import FieldItemAdditionalData from './FieldItemAdditionalData';
import FieldItemDependsOn from './FieldItemDependsOn';
import FormItemMetadata from './FormItemMetadata';
import FormItemIsPrefill from './FormItemIsPrefill';
import { useIntl } from 'react-intl';
import { IFeatures } from '../../../store/rolesAndPrivileges';

const defaultFieldProps = [
  'displayAs',
  'label',
  'content',
  'attachments',
  'additionalFieldData',
  'required',
  'isPrefill',
  'approvalRequired',
  'approvalType',
  'dependsOn',
];

const formFieldProps: { [key: string]: string[] } = {
  Choice: [
    ...defaultFieldProps,
    'answers',
    'defaultValue',
    'requiredAnswerCount',
    'metadata',
  ],
  Date: [...defaultFieldProps, 'metadata'],
  ImageUpload: [...defaultFieldProps, 'photoCount', 'metadata'],
  Input: [...defaultFieldProps, 'metadata'],
  Instruction: [...defaultFieldProps, 'metadata'],
  Section: ['label'],
  Signature: [...defaultFieldProps, 'legalText', 'metadata'],
  VideoUpload: [...defaultFieldProps, 'metadata'],
};

const formItemLayout = {
  labelCol: {
    xs: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 16, offset: 1 },
  },
};

interface Props {
  selectedNode?: FormChild;
  editMode: boolean;
  formInstance: FormInstance;
  isNewField?: boolean;
}

const FormGenerator: FC<Props> = ({
  selectedNode,
  editMode,
  formInstance,
  isNewField,
}) => {
  const { fieldId }: FormDetailParams = useParams();
  const intl = useIntl();
  const publishedFlatList = useSelector(
    (state: RootState) => state.formBuilderDetail.publishedFlatList
  );
  const privilegesState = useSelector(
    (state: RootState) => state.authentication?.privileges ?? {}
  );

  const form = useSelector((state: RootState) => state.formBuilderDetail.form);
  const [typeOptions, setTypeOptions] = useState<Option[]>([]);
  const [selectedFieldType, setSelectedFieldType] = useState<FormFieldType>();
  const [fieldChanges, setFieldChanges] = useState<FieldChanges | null>(null);

  const initialState: { [key: string]: any } = useMemo(
    () => ({
      type: selectedNode?.type,
      label: selectedNode?.label,
      displayAs: selectedNode?.displayAs,
      content: selectedNode?.content,
      additionalFieldData: selectedNode?.additionalFieldData,
      required: selectedNode?.required ?? true,
      isPrefill: selectedNode?.isPrefill || false,
      approvalRequired: selectedNode?.approvalRequired || false,
      approvalType: isNewField
        ? ApprovalType.SingleApprover
        : selectedNode?.approvalType,
      isValidate: selectedNode?.isValidate || false,
      range:
        {
          min: !!selectedNode?.validation?.conditions?.filter(
            (item) => item.operator === 'GreaterThanOrEqual'
          )[0]?.value
            ? parseFloat(
                selectedNode?.validation?.conditions
                  ?.filter((item) => item.operator === 'GreaterThanOrEqual')[0]
                  ?.value?.toString()
              )
            : undefined,
          max: !!selectedNode?.validation?.conditions?.filter(
            (item) => item.operator === 'LessThanOrEqual'
          )[0]?.value
            ? parseFloat(
                selectedNode?.validation?.conditions
                  ?.filter((item) => item.operator === 'LessThanOrEqual')[0]
                  ?.value.toString()
              )
            : undefined,
        } || {},
      validAnswers: selectedNode?.validation?.conditions,
      errorMessage: selectedNode?.validation?.message,
      attachments: selectedNode?.attachments,
      dependsOn: selectedNode?.dependsOn,
      answers: selectedNode?.answers,
      defaultValue: selectedNode?.defaultValue,
      requiredAnswerCount: {
        min: selectedNode?.requiredAnswerCount?.min || 1,
        max: selectedNode?.requiredAnswerCount?.max || 1,
      },
      photoCount: {
        min: selectedNode?.photoCount?.min || 1,
        max: selectedNode?.photoCount?.max || 1,
      },
      legalText: selectedNode?.legalText,
      commentRequired: selectedNode?.commentRequired,
      metadata: {
        dependOn: parseMetadataDependencies(
          selectedNode?.metadata?.dependOn || {}
        ),
        defaultValueKey: selectedNode?.metadata?.defaultValueKey,
        writeTo: selectedNode?.metadata?.writeTo,
      },
    }),
    [selectedNode, isNewField]
  );

  const getFieldItem = (item: keyof FormChild, id: number | string) => {
    switch (item) {
      case 'label':
        return (
          <FormItemLabel
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'displayAs':
        return (
          <FormItemDisplayAs
            key={id}
            editMode={editMode}
            isNewField={isNewField}
            formInstance={formInstance}
            fieldChanges={fieldChanges}
          />
        );
      case 'content':
        return (
          <FormItemContent
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'additionalFieldData':
        return (
          <FieldItemAdditionalData
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'required':
        return (
          <FormItemRequired
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'approvalRequired':
        return (
          <FormItemApprovalReq
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'attachments':
        return (
          <FormItemAttachments
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'dependsOn':
        return (
          <FieldItemDependsOn
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
            selectedNodeId={selectedNode?.id}
          />
        );
      case 'answers':
        return (
          <FormItemAnswers
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'requiredAnswerCount':
        return (
          <FormItemReqAnswersCount
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'photoCount':
        return (
          <FormItemPhotoCount
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'legalText':
        return (
          <FormItemLegalText
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      case 'metadata':
        return (
          <FormItemMetadata
            key={id}
            editMode={editMode && privilegesState[IFeatures.SuperAdminAccess]}
            fieldChanges={fieldChanges}
          />
        );
      case 'isPrefill':
        return (
          <FormItemIsPrefill
            key={id}
            editMode={editMode}
            fieldChanges={fieldChanges}
          />
        );
      default:
        return undefined;
    }
  };

  //When creating a new field, resets field items on field type change
  useEffect(() => {
    if (!selectedNode && selectedFieldType)
      formInstance.resetFields(formFieldProps[selectedFieldType]);
  }, [formInstance, selectedNode, selectedFieldType]);

  useEffect(() => {
    if (selectedNode?.type && !isNewField && form?.status !== 'Published') {
      const publishedField = publishedFlatList?.get(fieldId!);
      const fieldChanges = formFieldProps[selectedNode?.type].reduce(
        (changesObj, prop) => {
          const publishedItem = publishedField?.[prop];
          changesObj[prop] = {
            hasChanges: areItemsDifferent(selectedNode?.[prop], publishedItem),
            publishedItem,
          };
          return changesObj;
        },
        {} as FieldChanges
      );
      setFieldChanges(fieldChanges);
    }
  }, [selectedNode, fieldId, publishedFlatList, form?.status, isNewField]);

  useEffect(() => {
    if (selectedNode) setSelectedFieldType(selectedNode.type);
  }, [selectedNode, editMode]);

  //Resets field values to initial state on cancel edit
  useEffect(() => {
    formInstance.setFieldsValue(initialState);
  }, [formInstance, editMode, initialState]);

  //Sets the field type options list
  useEffect(() => {
    setTypeOptions(getOptions(FormFieldType));

    return () => setSelectedFieldType(undefined);
  }, []);

  return (
    <Form
      {...formItemLayout}
      form={formInstance}
      initialValues={initialState}
      className={styles.formGenerator}
      labelWrap
    >
      <Form.Item
        key={0}
        name="type"
        label={
          <Tooltip
            placement="topLeft"
            title={intl.formatMessage({ id: 'field_type_tooltip' })}
          >
            <span>{intl.formatMessage({ id: 'field_type' })} </span>
          </Tooltip>
        }
        rules={[{ required: true, message: 'Select the field type' }]}
      >
        <Select
          disabled={!editMode || !!selectedNode}
          options={typeOptions}
          value={selectedFieldType}
          onChange={(value) => setSelectedFieldType(value)}
        />
      </Form.Item>
      {selectedFieldType &&
        formFieldProps[selectedFieldType].map((prop, i) =>
          getFieldItem(prop as keyof FormChild, i + 1)
        )}
    </Form>
  );
};

export default FormGenerator;
